import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { X, Loader, Save, MapPin, GitBranch, AlertTriangle, Maximize2, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import OrderPathwayBuilder, { QuestionsTab } from './OrderPathwayBuilder';
import { savePathway, transformMentionsForLoad } from '../../services/pathwayService';
import { getSupabaseClient } from '../../supabaseClient';
import { useBusinessType } from '../../context/BusinessContext';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { cn } from '../../lib/utils';

import { useLocations } from '../../hooks/use-locations';
import { Skeleton } from '../ui/skeleton';

// Animation variants
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.2 } }
};

const modalVariants = {
  hidden: { scale: 0.95, opacity: 0, y: 30 },
  visible: { 
    scale: 1, 
    opacity: 1, 
    y: 0,
    transition: { type: "spring", damping: 30, stiffness: 300, duration: 0.4 }
  },
  exit: { 
    scale: 0.98, 
    opacity: 0, 
    y: 20, 
    transition: { duration: 0.2 } 
  }
};

// --- Skeleton Component for OrderPathwayModal ---
const OrderPathwaySkeleton = () => {
  return (
    <div className="space-y-4 sm:space-y-6 py-3 sm:py-4 animate-pulse">
      {/* Details Tab Skeleton */}
      {/* Name */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-10 w-full" />
      </div>
      {/* Description */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-20 w-full" />
      </div>
      {/* Category */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-1/5" />
        <Skeleton className="h-10 w-full" />
      </div>
      {/* Enable Connection Toggle */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="space-y-1 w-3/4">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-3/4" />
        </div>
        <Skeleton className="h-6 w-12 rounded-full" />
      </div>
      {/* Location Variants Toggle */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="space-y-1 w-3/4">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-3/4" />
        </div>
        <Skeleton className="h-6 w-12 rounded-full" />
      </div>
    </div>
  );
};
// --- End Skeleton Component ---

// Helper function to fetch action-specific data for a given complex_action_id and action_type
// Now accepts allQuestionsList for transforming mentions on load
const fetchActionDataForVariant = async (supabase, complexActionId, actionType, allQuestionsList) => {
    let toolConnection = { actionType: actionType };

    try {
        if (actionType === 'api') {
            const { data: apiRow } = await supabase
                .from('brain_complex_actions_api')
                .select('*')
                .eq('complex_action_id', complexActionId)
                .maybeSingle();

            if (apiRow) {
                toolConnection.apiEndpoint = apiRow.url || '';
                toolConnection.apiMethod = apiRow.method || 'POST';
                toolConnection.apiHeaders = apiRow.headers || {};
                toolConnection.apiBody = apiRow.body ? transformMentionsForLoad(JSON.stringify(apiRow.body, null, 2), allQuestionsList) : '';
            } else {
                toolConnection.apiEndpoint = '';
                toolConnection.apiMethod = 'POST';
                toolConnection.apiHeaders = {};
                toolConnection.apiBody = '';
            }
        } else if (actionType === 'sms') {
            const { data: smsRow } = await supabase
                .from('brain_complex_actions_sms')
                .select('*')
                .eq('complex_action_id', complexActionId)
                .maybeSingle();

            if (smsRow) {
                toolConnection.smsMessage = transformMentionsForLoad(smsRow.message || '', allQuestionsList);
                toolConnection.smsUseContactPhone = smsRow.use_contact_phone || false;
                if (smsRow.use_contact_phone) {
                    const { data: numData } = await supabase
                        .from('brain_complex_actions_sms_numbers')
                        .select('phone_number')
                        .eq('complex_action_sms_id', smsRow.id);
                    toolConnection.smsNumbers = (numData || []).map(n => n.phone_number);
                } else {
                    toolConnection.smsNumbers = [];
                }
            } else {
                toolConnection.smsMessage = '';
                toolConnection.smsUseContactPhone = false;
                toolConnection.smsNumbers = [];
            }
        } else if (actionType === 'email') {
             const { data: emailRow } = await supabase
                .from('brain_complex_actions_email')
                .select('*')
                .eq('complex_action_id', complexActionId)
                .maybeSingle();

            if (emailRow) {
                toolConnection.emailSubject = transformMentionsForLoad(emailRow.subject || '', allQuestionsList);
                toolConnection.emailMessage = transformMentionsForLoad(emailRow.message || '', allQuestionsList);
                toolConnection.emailUseContactEmail = emailRow.use_contact_email || false;
                if (emailRow.use_contact_email) {
                     const { data: addrData } = await supabase
                        .from('brain_complex_actions_email_addresses')
                        .select('email_address')
                        .eq('complex_action_email_id', emailRow.id);
                    toolConnection.emailAddresses = (addrData || []).map(a => a.email_address);
                } else {
                    toolConnection.emailAddresses = [];
                }
            } else {
                toolConnection.emailSubject = '';
                toolConnection.emailMessage = '';
                toolConnection.emailUseContactEmail = false;
                toolConnection.emailAddresses = [];
            }
        }
        // TODO: Add logic for 'transfer' messages if applicable, using transformMentionsForLoad
    } catch (error) {
        console.error(`Error fetching action data for action ${complexActionId}, type ${actionType}:`, error);
        toast.error(`Failed to load specific configuration for action type ${actionType}`);
        // Return default connection for this type on error?
        toolConnection = { actionType: actionType };
    }

    return toolConnection;
};

const OrderPathwayModal = ({ isOpen, onClose, pathwayIdToEdit }) => {
  const { businessType } = useBusinessType();
  const { locations: fetchedLocations, loading: locationsLoading } = useLocations(businessType);
  const supabase = getSupabaseClient(businessType);

  const [isLoading, setIsLoading] = useState(false);
  const [pathwayConfig, setPathwayConfig] = useState(null);
  const [editMode, setEditMode] = useState(!!pathwayIdToEdit);
  const [currentPathwayId, setCurrentPathwayId] = useState(pathwayIdToEdit);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [activeTab, setActiveTab] = useState('general');
  const initializedRef = useRef(false);

  // --- State for Location Variants ---
  const [showLocationVariants, setShowLocationVariants] = useState(false);
  const [locationGroupings, setLocationGroupings] = useState([]);
  const [locationAssignments, setLocationAssignments] = useState({});
  const [activeLocationGrouping, setActiveLocationGrouping] = useState(null);
  const [formDataByGrouping, setFormDataByGrouping] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // --- NEW: State for expanded questions view ---
  const [isQuestionsExpanded, setIsQuestionsExpanded] = useState(false);
  // --- END NEW State ---

  // Initialize pathway: Fetch existing if ID provided, otherwise create new
  useEffect(() => {
    const initializePathway = async () => {
      if (!isOpen) {
        // Reset state when modal is closed
        setPathwayConfig(null);
        setIsLoading(false);
        setEditMode(!!pathwayIdToEdit);
        setCurrentPathwayId(pathwayIdToEdit);
        setValidationErrors({});
        setIsSubmitting(false);
        setActiveTab('general');
        // Reset variant state
        setShowLocationVariants(false);
        setLocationGroupings([]);
        setLocationAssignments({});
        setActiveLocationGrouping(null);
        setFormDataByGrouping({});
        setHasUnsavedChanges(false);
        initializedRef.current = false; // Reset initialized flag
        return;
      }

      // Prevent re-initialization if already done for this open instance
      if (initializedRef.current) return;

      setIsLoading(true);
      setEditMode(!!pathwayIdToEdit);
      setCurrentPathwayId(pathwayIdToEdit);
      setValidationErrors({}); // Reset errors on open
      setActiveTab('general');
      setHasUnsavedChanges(false);

      try {
        let loadedConfig = null;
        let loadedGroupings = [];
        let loadedAssignments = {};
        let primaryGroupingId = null;
        let loadedFormDataByGrouping = {};
        let shouldShowVariants = false;

        if (pathwayIdToEdit) {
          // --- Fetch existing pathway logic ---
          // Fetch the PRIMARY complex action using the ID passed in
          const { data: primaryAction, error: primaryActionErr } = await supabase
            .from('brain_complex_actions')
            .select('*, settings_id(id, name, description, triggers, category_id)') // Join settings
            .eq('id', pathwayIdToEdit) // Use the passed ID
            .single();
          if (primaryActionErr || !primaryAction) throw primaryActionErr || new Error('Primary complex action not found');

          const settings = primaryAction.settings_id;
          if (!settings) throw new Error('Associated settings not found');
          const settingsId = settings.id; // Store for later use

          // Fetch ALL groupings for this setting
          const { data: groupingsData, error: groupingsErr } = await supabase
            .from('brain_groupings')
            .select('*')
            .eq('settings_id', settingsId);
          if (groupingsErr) throw groupingsErr;
          loadedGroupings = groupingsData || [];

          // Fetch ALL complex actions for this setting to get variant data
          const { data: allActionsData, error: allActionsErr } = await supabase
              .from('brain_complex_actions')
              .select('id, grouping_id, action_type, connection_action_id') // REMOVED connection_id
              .eq('settings_id', settingsId);
          if (allActionsErr) throw allActionsErr;
          const allActionsMap = (allActionsData || []).reduce((acc, action) => {
              if (action.grouping_id) { // Ensure grouping_id exists
                  acc[action.grouping_id] = action;
              }
              return acc;
          }, {});

          // Fetch Location Assignments if groupings exist
           if (loadedGroupings.length > 0) {
            const groupIds = loadedGroupings.map(g => g.id);
            const { data: locData, error: locErr } = await supabase
              .from('brain_grouping_locations')
              .select('grouping_id, location_id')
              .in('grouping_id', groupIds);
            if (locErr) throw locErr;
            (locData || []).forEach(row => {
              if (!loadedAssignments[row.grouping_id]) loadedAssignments[row.grouping_id] = [];
              loadedAssignments[row.grouping_id].push(row.location_id);
            });
          }

          // --- Fetch Action Data for EACH Grouping, passing the combined questions list --- 
          loadedFormDataByGrouping = {}; // Reset
          // First, gather all questions from the primary action for transformation lookup
          const { data: fieldsDataForTransform, error: fieldsDataErrTransform } = await supabase
            .from('brain_complex_actions_fields')
            .select('id, question') // question is the text
            .eq('brain_complex_actions_id', primaryAction.id);
          if (fieldsDataErrTransform) throw fieldsDataErrTransform;
          const allQuestionsForLoad = (fieldsDataForTransform || []).map(f => ({ id: f.id, text: f.question }));

          for (const group of loadedGroupings) {
              const actionForGroup = allActionsMap[group.id];
              if (actionForGroup) {
                  const variantToolConnection = await fetchActionDataForVariant(
                      supabase,
                      actionForGroup.id,
                      actionForGroup.action_type,
                      allQuestionsForLoad // Pass the list of all questions here
                  );
                  // Add connection info if present
                  variantToolConnection.connectionId = actionForGroup.connection_id;
                  variantToolConnection.actionId = actionForGroup.connection_action_id;

                  loadedFormDataByGrouping[group.id] = { toolConnection: variantToolConnection };
              } else {
                  // Handle case where an action might be missing for a grouping (shouldn't normally happen)
                  loadedFormDataByGrouping[group.id] = { toolConnection: { actionType: primaryAction.action_type } }; // Default to primary action type
              }
          }

          // Determine initial state based on fetched data
          shouldShowVariants = loadedGroupings.length > 1;
          // Use the grouping_id from the primary action we fetched
          primaryGroupingId = primaryAction.grouping_id;

          // --- Build Initial `pathwayConfig` ---
          // Base config from settings
           loadedConfig = {
            name: settings?.name || '',
            description: settings?.description || '',
            triggers: settings?.triggers || [], // Common triggers
            category_id: settings?.category_id || '',
            settings_id: settings.id, // Store settings_id
            grouping_id: primaryGroupingId, // Store the primary/current grouping ID
            complex_action_id: primaryAction.id // Store the primary action ID
            // Fields below are common and fetched once
            // qualifyingFailureRoute: '', // TODO: Fetch if exists
            // postSuccessEnabled: false, // TODO: Fetch if exists
            // postSuccessActions: [], // TODO: Fetch if exists
          };

          // Fetch fields/enums based on the *primary* action ID
          const { data: fields, error: fieldsErr } = await supabase
            .from('brain_complex_actions_fields')
            .select('*') // Select all fields, including id, parent_field_id, criteria, order
            .eq('brain_complex_actions_id', primaryAction.id) // Use primary action ID
            // .order('order'); // DB already stores relative order, let frontend sort hierarchically for display
          if (fieldsErr) throw fieldsErr;

           const mapDbToUi = (dbType) => ({ 'numeric': 'numeric', 'boolean': 'checkbox', 'enum': 'radio', 'string': 'text' }[dbType] || 'text');
           const fieldIds = fields.map(f => f.id);
           let enumsMap = {};
           if (fieldIds.length) {
             const { data: enums, error: enumsErr } = await supabase
               .from('brain_complex_actions_fields_enum')
               .select('*')
               .in('complex_actions_fields_id', fieldIds);
             if (!enumsErr) {
               enums.forEach(en => {
                 if (!enumsMap[en.complex_actions_fields_id]) enumsMap[en.complex_actions_fields_id] = [];
                 enumsMap[en.complex_actions_fields_id].push(en.enum);
               });
             }
           }
           const qualifyingQuestions = []; 
           const orderQuestions = [];

           fields.forEach((f) => {
             const qObj = {
               id: f.id, // Use DB UUID as the question ID
               type: mapDbToUi(f.type),
               text: f.question,
               required: true, // Assuming required, or fetch from f.required if it exists
               options: f.type === 'enum' ? (enumsMap[f.id] || []) : [],
               isEditable: f.is_editable ?? true, 
               parentFieldId: f.parent_field_id || null, // Use DB parent_field_id (should be UUID)
               criteria: f.criteria || null, // Load criteria JSONB
               dbOrder: f.order, // Store the database order
               isQualifying: f.is_qualifying || false // Store isQualifying
             };

             if (f.is_qualifying) {
                qualifyingQuestions.push(qObj);
             } else {
                orderQuestions.push(qObj);
             }
           });
           // Sort qualifying and top-level order questions by their dbOrder before assigning
           qualifyingQuestions.sort((a,b) => a.dbOrder - b.dbOrder);
           // For orderQuestions, top-level ones need sorting by dbOrder. Children will be handled by hierarchy builder.
           // The initial flat list for orderQuestions should be generally ordered by parents then children due to how they are likely inserted/retrieved.

          loadedConfig.qualifyingQuestions = qualifyingQuestions;
          loadedConfig.orderQuestions = orderQuestions;

          // Combine for global use in the modal, e.g., for passing to MentionTextarea later if needed directly
          // This is the same list we passed to fetchActionDataForVariant
          const allLoadedQuestions = [...qualifyingQuestions, ...orderQuestions];

          // Get the toolConnection for the *primary* grouping from the pre-fetched data
          // This data has now been transformed by fetchActionDataForVariant
          const primaryVariantData = loadedFormDataByGrouping[primaryGroupingId] || {};
          loadedConfig.toolConnection = primaryVariantData.toolConnection || { actionType: primaryAction.action_type }; // Fallback needed

          // Derive connectionEnabled from the loaded primary toolConnection
          loadedConfig.connectionEnabled = !!loadedConfig.toolConnection.connectionId;

        } else {
          // --- Create a blank new pathway config ---
          loadedConfig = {
            name: '', description: '', connectionEnabled: false, // Default to false for new pathways
            qualifyingQuestions: [], qualifyingFailureRoute: '', orderQuestions: [],
            // Default toolConnection for new pathway
            toolConnection: { actionType: 'api', apiEndpoint: '', apiMethod: 'POST', apiHeaders: {}, apiBody: '' },
            postSuccessEnabled: false, postSuccessActions: [],
            triggers: [], category_id: '',
            settings_id: null, grouping_id: null, complex_action_id: null // No IDs yet
          };
            const defaultId = 'default_group';
            loadedGroupings = [{ id: defaultId, name: 'Default', isNew: true }];
            // Use available locations or empty array if locations are still loading
            const availableLocations = fetchedLocations || [];
            loadedAssignments = { [defaultId]: availableLocations.map(l => l.id) };
            primaryGroupingId = defaultId;
            shouldShowVariants = false; // Don't show variants by default for new
            // Store initial connection data using the default grouping ID
            loadedFormDataByGrouping = { [defaultId]: { toolConnection: { ...loadedConfig.toolConnection } } };
        }

        // Set all the state at once
        setPathwayConfig(loadedConfig);
        setLocationGroupings(loadedGroupings);
        setLocationAssignments(loadedAssignments);
        setFormDataByGrouping(loadedFormDataByGrouping);
        setActiveLocationGrouping(primaryGroupingId);
        setShowLocationVariants(shouldShowVariants);

      } catch (error) {
         console.error('Error initializing pathway in modal:', error);
        toast.error('Failed to load pathway data: ' + error.message);
        setPathwayConfig(null); // Ensure builder doesn't render on error
      } finally {
        setIsLoading(false);
        initializedRef.current = true; // Mark as initialized
      }
    };

    // Initialize immediately when modal opens, don't wait for locations
    initializePathway();

  }, [isOpen, pathwayIdToEdit, supabase]); // Removed locationsLoading and fetchedLocations dependencies

  // --- Handler for changes from LocationVariantsSidePanel ---
  const handleLocationVariantsChange = useCallback((data) => {
    setLocationGroupings(data.groupings);
    setLocationAssignments(data.assignedLocations);
    setHasUnsavedChanges(true);

    if (data.selectedGrouping && data.selectedGrouping !== activeLocationGrouping) {
      // Store only the TOOL CONNECTION of the previously active variant
      if (activeLocationGrouping && pathwayConfig?.toolConnection) {
        setFormDataByGrouping(prev => ({
          ...prev,
          [activeLocationGrouping]: { 
            toolConnection: pathwayConfig.toolConnection 
          }
        }));
      }

      // Load data for the newly selected group
      const newGroupData = formDataByGrouping[data.selectedGrouping] || {};
      setPathwayConfig(prev => {
        // Keep common fields (name, desc, category, triggers, questions)
        // Keep the COMMON actionType, but load variant-specific config for it
        const commonActionType = prev.toolConnection?.actionType;
        const variantToolConnectionConfig = newGroupData.toolConnection || {};

        return {
          ...prev, 
          toolConnection: {
            ...(prev.toolConnection || {}), // Keep existing common fields like actionType
            ...variantToolConnectionConfig, // Overwrite with variant-specific config
            actionType: commonActionType // Ensure common actionType is preserved
          },
          grouping_id: data.selectedGrouping // Update the current grouping ID
        };
      });

      setActiveLocationGrouping(data.selectedGrouping);
    } else if (!data.selectedGrouping && activeLocationGrouping) {
      // If the side panel deselects (e.g., last group deleted), reset active grouping
      setActiveLocationGrouping(null);
      // Keep the current pathwayConfig as is, maybe representing a default?
    }
  }, [activeLocationGrouping, formDataByGrouping, pathwayConfig]); // Added pathwayConfig dependency

  // --- Handler for form changes when variants are enabled ---
  const handleFormChangeWithVariants = useCallback((name, value) => {
     if (!activeLocationGrouping) return; // Should not happen if variants are on

     const isActionTypeField = name === 'toolConnection.actionType';
     const isToolConnectionField = name.startsWith('toolConnection.');

     // Update the main pathwayConfig for immediate UI feedback (ALWAYS)
     setPathwayConfig(prev => {
        if (isActionTypeField) {
           // Update only the actionType in the main config
           return {
             ...prev,
             toolConnection: { 
               ...(prev.toolConnection || {}), 
               actionType: value,
               // Reset other toolConnection fields when type changes?
               // Maybe clear endpoint, headers, body, message etc.?
               // For simplicity now, let's just update the type.
             } 
           };
        } else if (isToolConnectionField) {
           const key = name.split('.')[1];
           return {
             ...prev,
             toolConnection: { ...(prev.toolConnection || {}), [key]: value }
           };
        } else if (name === 'qualifyingQuestions' || name === 'orderQuestions' || name === 'triggers') {
           // Handle array updates directly
           return { ...prev, [name]: value };
        } else {
          // Handle direct top-level updates (name, description, category etc.)
          return { ...prev, [name]: value };
        }
     });

     // --- Update the stash ONLY for toolConnection fields *EXCEPT* actionType --- 
     if (isToolConnectionField && !isActionTypeField) {
       const key = name.split('.')[1];
       setFormDataByGrouping(prev => ({
          ...prev,
          [activeLocationGrouping]: {
             ...(prev[activeLocationGrouping] || {}),
             toolConnection: { 
               ...(prev[activeLocationGrouping]?.toolConnection || {}), 
               [key]: value 
             }
          }
       }));
     }
     // --- Do NOT update stash for common fields OR the actionType itself --- 

     setHasUnsavedChanges(true);
  }, [activeLocationGrouping]); // Removed pathwayConfig from deps here, rely on prev state

  // Validation Function (moved from builder)
  const validatePathway = () => {
    const errors = {};
    if (!pathwayConfig) return errors; // Should not happen

    // Validate based on the *currently viewed* pathwayConfig
    if (!pathwayConfig.name?.trim()) errors.name = "Name is required";
    if (!pathwayConfig.category_id) errors.category_id = "Category is required";
    if ((pathwayConfig.qualifyingQuestions?.length || 0) + (pathwayConfig.orderQuestions?.length || 0) === 0) {
       errors.questions = "At least one question is required";
    }
    // TODO: Add more validation for specific fields (e.g., toolConnection based on type)

    // If variants are enabled, maybe loop through formDataByGrouping to validate all variants?
    // For simplicity now, we only validate the active view.
    // if (showLocationVariants) {
    //    Object.entries(formDataByGrouping).forEach(([groupId, groupData]) => {
    //       // Validate groupData... add errors keyed by group?
    //    });
    // }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save Function (adapted - complex save logic deferred)
  const handleSave = async () => {
    if (!validatePathway()) {
       toast.error("Please fix the errors before saving.");
       // Switch to the first tab with an error (based on current pathwayConfig errors)
       if (validationErrors.name || validationErrors.category_id) setActiveTab('general');
       else if (validationErrors.questions) setActiveTab('questions');
       else if (validationErrors.triggers) setActiveTab('triggers');
       // Add more checks if needed (e.g., toolConnection validation)
       return;
    }

    setIsSubmitting(true);
    try {
      // --- Prepare data for saving ---
      let dataToSave;

      // Store the currently displayed config back into the stash before saving
      // This ensures the last viewed variant's data is included
      const finalFormDataByGrouping = { ...formDataByGrouping };
      if (activeLocationGrouping && pathwayConfig) {
        // Extract only the variant-specific parts to store
        // Avoid storing the entire config which duplicates common fields
        // Ensure toolConnection exists before trying to access it
        if (pathwayConfig.toolConnection) {
            finalFormDataByGrouping[activeLocationGrouping] = {
                toolConnection: pathwayConfig.toolConnection
                // NO LONGER STORING questions/triggers here
            };
        } else {
            // Handle case where toolConnection might be null/undefined for the active group
            finalFormDataByGrouping[activeLocationGrouping] = {
                toolConnection: {}
            };
        }
      }

      if (showLocationVariants) {
          // Prepare data for saving multiple variants
          dataToSave = {
              // Common fields (taken from the main pathwayConfig)
              settings_id: pathwayConfig.settings_id,
              complex_action_id: pathwayConfig.complex_action_id, // Pass if updating
              name: pathwayConfig.name,
              description: pathwayConfig.description,
              category_id: pathwayConfig.category_id,
              triggers: pathwayConfig.triggers, // Common triggers
              qualifyingQuestions: pathwayConfig.qualifyingQuestions, // Common questions
              orderQuestions: pathwayConfig.orderQuestions, // Common questions
              // Variant-specific data
              isVariantMode: true,
              groupings: locationGroupings, // Array of grouping objects {id, name, isNew?}
              assignments: locationAssignments, // Object { [groupingId]: [locationId, ...] }
              // variantsData should now ONLY contain toolConnection per group
              variantsData: finalFormDataByGrouping // Object { [groupingId]: { toolConnection } }
          };
      } else {
          // Prepare data for saving a single configuration
          const defaultGroupId = locationGroupings.length > 0 ? locationGroupings[0].id : 'default'; // Get the ID of the single group
          dataToSave = {
              // Common fields
              settings_id: pathwayConfig.settings_id,
              complex_action_id: pathwayConfig.complex_action_id, // Pass if updating
              name: pathwayConfig.name,
              description: pathwayConfig.description,
              category_id: pathwayConfig.category_id,
              triggers: pathwayConfig.triggers, // Common triggers
              qualifyingQuestions: pathwayConfig.qualifyingQuestions, // Common questions
              orderQuestions: pathwayConfig.orderQuestions, // Common questions
              // Single configuration data (treat as one variant)
              isVariantMode: false,
              groupings: [{ id: defaultGroupId, name: 'Default' }], // Pass the single grouping info
              assignments: { [defaultGroupId]: pathwayConfig.all_locations ? [] : pathwayConfig.selected_locations },
              variantsData: { // Store the single config under the default group ID
                  [defaultGroupId]: {
                      toolConnection: pathwayConfig.toolConnection
                      // NO LONGER STORING questions/triggers here
                  }
              }
          };
      }

      // Call the updated savePathway service function
      // Note: savePathway needs to be updated to handle this new structure
      // The savePathway function itself needs to be adjusted to expect questions/triggers at the top level
      // when isVariantMode is true.
      await savePathway(dataToSave, editMode ? currentPathwayId : null);
      // await new Promise(resolve => setTimeout(resolve, 500)); // Keep simulation for now

      toast.success(editMode ? 'Pathway updated successfully' : 'New pathway created successfully');
      setHasUnsavedChanges(false);
      onClose(true); // Close and indicate success
    } catch (error) {
      console.error('Error saving pathway from modal:', error);
      toast.error('Failed to save pathway: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close attempt
   const handleCloseAttempt = () => {
      // TODO: Add unsaved changes check using hasUnsavedChanges state
      onClose(false); // Close without saving
   };


  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[10000] overflow-y-auto flex items-start justify-center pt-2 sm:pt-10 md:pt-20 pb-4 sm:pb-10">
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={backdropVariants}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            onClick={handleCloseAttempt} // Use close attempt handler
          />

          {/* Modal Panel - Adjusted style */}
          <motion.div
            key="modal"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={modalVariants}
            className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-2 sm:mx-4 flex flex-col h-[80vh] sm:h-[75vh] overflow-hidden border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Consistent Style */}
            <div className="flex items-center justify-between p-4 sm:px-6 sm:py-4 border-b border-gray-100 flex-shrink-0 bg-white z-20">
              <div className="flex items-center">
                <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0",
                    "bg-green-100 text-green-700" 
                )}>
                    <GitBranch className="h-4 w-4" /> 
                </div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                  {editMode ? 'Edit Complex Action' : 'Create Complex Action'}
                </h2>
              </div>
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="p-1 text-gray-500 hover:text-gray-800 rounded-full"
                onClick={handleCloseAttempt}
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </motion.button>
            </div>

            {/* Tabs Navigation - Added below header */}
            <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2 bg-white z-10 border-b border-gray-50 flex-shrink-0">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className={cn(
                    "grid bg-gray-100 p-1 rounded-lg",
                    editMode ? "grid-cols-5" : "grid-cols-4"
                )}>
                  <TabsTrigger 
                    value="general"
                    className={cn(
                      "rounded-md py-1.5 text-xs sm:text-sm font-medium transition-all relative flex items-center justify-center",
                      "data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm"
                    )}
                  >
                    Details
                    {(validationErrors.name || validationErrors.category_id) && (
                      <span className="absolute top-1 right-1 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-red-500 rounded-full" />
                    )}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="triggers"
                    className={cn(
                      "rounded-md py-1.5 text-xs sm:text-sm font-medium transition-all relative flex items-center justify-center",
                      "data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm"
                    )}
                  >
                    Triggers
                  </TabsTrigger>
                  <TabsTrigger 
                    value="questions"
                    className={cn(
                      "rounded-md py-1.5 text-xs sm:text-sm font-medium transition-all relative flex items-center justify-center",
                      "data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm"
                    )}
                  >
                    Questions
                    {validationErrors.questions && (
                      <span className="absolute top-1 right-1 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-red-500 rounded-full" />
                    )}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="actions"
                    className={cn(
                      "relative rounded-md py-1.5 text-xs sm:text-sm font-medium transition-all flex items-center justify-center",
                      "data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm"
                    )}
                  >
                    {/* Grouping badge */}
                    {showLocationVariants && activeLocationGrouping && locationGroupings.length > 0 && (
                      <span className="pointer-events-none absolute -top-1 -right-1.5 bg-blue-50 text-blue-600 text-[8px] leading-none px-1 py-[0.094rem] rounded-sm ring-1 ring-blue-200">
                        {locationGroupings.find(g => g.id === activeLocationGrouping)?.name || 'Default'}
                      </span>
                    )}
                    Actions
                  </TabsTrigger>
                  {/* Add Danger Tab Trigger conditionally */}
                  {editMode && (
                    <TabsTrigger 
                      value="danger"
                      className={cn(
                        "rounded-md py-1.5 text-xs sm:text-sm font-medium transition-all relative flex items-center justify-center",
                        "data-[state=active]:bg-white data-[state=active]:text-red-600 data-[state=active]:shadow-sm",
                        "text-red-500/70 hover:text-red-500"
                      )}
                    >
                      {/* Remove the icon */}
                      {/* <AlertTriangle className="h-4 w-4 sm:mr-1.5" /> */}
                      {/* Remove responsive classes to show text on mobile */}
                      <span>Danger</span>
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
            </div>

            {/* Content Body - Scrollable */}
            <div className="flex-grow overflow-y-auto relative">
              {(isLoading || locationsLoading) ? (
                // Show Skeleton while loading
                <div className="absolute inset-0 bg-white z-10 p-4 sm:p-6">
                  <OrderPathwaySkeleton />
                </div>
              ) : pathwayConfig ? (
                // Render main builder content if not on danger tab
                activeTab !== 'danger' ? (
                   <div className="px-4 sm:px-6">
                     <OrderPathwayBuilder
                       pathwayConfig={pathwayConfig}
                       setPathwayConfig={setPathwayConfig} // Pass direct setter
                       validationErrors={validationErrors}
                       isModal={true}
                       activeTab={activeTab}
                       // --- Pass variant props ---
                       locations={fetchedLocations}
                       showLocationVariants={showLocationVariants}
                       setShowLocationVariants={setShowLocationVariants} // Pass setter for the toggle
                       activeLocationGrouping={activeLocationGrouping}
                       handleFormChangeWithVariants={handleFormChangeWithVariants} // Pass variant handler
                       setHasUnsavedChanges={setHasUnsavedChanges} // Pass setter for unsaved changes
                       // --- NEW: Pass onExpandClick to OrderPathwayBuilder ---
                       onExpandClick={() => setIsQuestionsExpanded(true)}
                     />
                   </div>
                ) : (
                  // Render Danger Zone content if on danger tab and in edit mode
                  <motion.div 
                    key="danger-zone"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="p-6 text-center max-w-lg mx-auto"
                  >
                    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <AlertTriangle className="h-8 w-8 text-red-500" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Complex Action</h3>
                    <p className="text-sm text-gray-500 mb-6">
                      This action cannot be undone. Deleting this complex action will remove it permanently, along with its settings, triggers, questions, and location variants.
                    </p>
                    <Button
                      variant="destructive"
                      // TODO: Add onClick handler for deletion confirmation
                      // onClick={() => setIsDeleteConfirmOpen(true)} 
                    >
                      Delete This Complex Action
                    </Button>
                  </motion.div>
                )
              ) : (
                <div className="flex items-center justify-center h-full p-6">
                  <p className="text-gray-600">Could not load pathway configuration.</p>
                </div>
              )}
            </div>

            {/* Fixed Footer - Conditionally render based on activeTab */}
            {!(isLoading || locationsLoading) && pathwayConfig && activeTab !== 'danger' && (
              <div className="border-t border-gray-100 py-3 sm:py-4 px-4 sm:px-6 bg-gray-50 flex justify-between items-center z-20 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseAttempt} // Use close attempt handler
                  className="text-gray-500"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSubmitting || isLoading} // Still disable if main isLoading is true, just don't show spinner
                  size="sm"
                  className="bg-gray-900 hover:bg-gray-800 text-white min-w-[100px]"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-1">
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Saving...</span>
                    </span>
                  ) : (
                    <span>{editMode ? 'Save' : 'Create'}</span>
                  )}
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  // Render side panel outside the main modal portal structure if needed
  // Or ensure it's part of the main return if portal isn't used here
  return (
    <>
      {isOpen ? createPortal(modalContent, document.body) : null}


      {/* --- NEW: Expanded Questions View Modal --- */}
      <AnimatePresence>
        {isQuestionsExpanded && (
          <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <motion.div
              key="expanded-questions-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 20, stiffness: 200 }}
              className="bg-white w-screen h-screen flex flex-col"
            >
              {/* Header for Expanded View */}
              <div className="flex items-center justify-between p-4 border-b bg-gray-50 flex-shrink-0">
                <h3 className="text-lg font-medium text-gray-800">Questions Builder</h3>
                <Button variant="ghost" size="icon" onClick={() => setIsQuestionsExpanded(false)} className="text-gray-500 hover:text-gray-700">
                  <Minimize2 className="h-5 w-5" />
                </Button>
              </div>
              {/* Content - Render QuestionsTab directly */}
              <div className="flex-grow overflow-y-auto p-6">
                {pathwayConfig && (
                  <QuestionsTab
                    pathwayConfig={pathwayConfig} // From OrderPathwayModal state
                    setPathwayConfig={setPathwayConfig} // Direct setter, or use variant handler if needed
                    showLocationVariants={showLocationVariants} // Pass variant state
                    handleFormChangeWithVariants={handleFormChangeWithVariants} // Pass variant handler
                    setHasUnsavedChanges={setHasUnsavedChanges} // Pass unsaved changes setter
                    // No onExpandClick needed here as this IS the expanded view
                  />
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* --- END NEW Expanded Questions View --- */}
    </>
  );
};

export default OrderPathwayModal; 